import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ProjectFinancialService } from '../project-financial.service';
import { CapexImportRow, FinancialCapex } from '../../../models/project/financial/financial-capex.model';
import { CommunicationService } from '../../../communication/communication.service';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import Utils from '../../../util';
import { ProjectService } from '../../project.service';
import { EstimationItem } from '../../../models/data-structures/estimation-item.model';
import { ProgressPopupDialogComponent } from '../../progress-sidebar/progress-popup/progress-popup.component';
import { ProjectValidation, ValidationSectionName } from '../../../models/data-structures/project-validation';
import { MatDialog } from '@angular/material/dialog';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { ValidationService } from '../../../helper/validation/validation.service';
import { UnitValue } from '../../../models/data-structures/unit-value.model';
import { UnitValueValidation } from '../../../helper/validation/validation-sub-modules/unit-value.validation';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import {
  DefaultCapexOpexEstNames
} from '../../../models/enums/project/financial/capex-opex-est/default-capex-opex-est-names.enum';
import {
  DefaultCapexEstPercentages
} from '../../../models/enums/project/financial/capex-opex-est/default-capex-est-percentages.class';
import { PeriodType } from '../../../models/project/plant-specifications/fuel.model';
import { ProductType } from '../../../models/project/plant-specifications/product.model';
@Injectable({
  providedIn: 'root'
})
export class FinancialCapexService implements OnDestroy {
  onDestroyNotifier = new EventEmitter();
  capex: FinancialCapex[] = [];
  capexLoadedNotifier = new BehaviorSubject<boolean>(false);
  capexUpdatedNotifier = new BehaviorSubject<boolean>(false);
  unsavedChangesNotifier = new BehaviorSubject<boolean>(false);
  private savedTotalCapexUnit: string;
  private defaultLifetime = new UnitValue();
  private periodUnits: Unit[] = null;
  defaultItemsReady = false;
  financialServiceLoaded = false;

  constructor(
    private financialService: ProjectFinancialService,
    private commService: CommunicationService,
    private projectCommService: ProjectCommunicationService,
    private projectService: ProjectService,
    private unitService: UnitService,
    private dialog: MatDialog,
    private toastrService: ConfiguredToastrService,
    private validationService: ValidationService,
    public translationService: AnessaTranslationService
  ) {
    this.financialService.financialFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        this.financialServiceLoaded = loaded
        if (loaded) {
          this.setDefaultLifetime();
          this.capex = this.financialService.getCapex();
          this.savedTotalCapexUnit = this.financialService.getSavedTotalCapexUnit();
          this.financialService.initializeDefaultItemCalculation();
        } else {
          this.capex = [];
          this.savedTotalCapexUnit = null;
        }
        this.checkIfLoaded();
      });
    this.financialService.financialUpdatedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          this.capex = this.financialService.getCapex();
          this.savedTotalCapexUnit = this.financialService.getSavedTotalCapexUnit();
          this.capexUpdatedNotifier.next(true);
        }
      });
    this.financialService.defaultItemsReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(ready => {
        this.defaultItemsReady = ready;
        this.checkIfLoaded();
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  private checkIfLoaded() {
    this.capexLoadedNotifier.next(this.financialServiceLoaded && this.defaultItemsReady);
  }

  getCapex() {
    return this.capex;
  }

  getMaxCapexLifetime() {
    const lifetimesInDays = this.capex.map(aCapex => aCapex.lifetime.value);
    if (!lifetimesInDays.length) return null;
    return Math.max(...lifetimesInDays);
  }

  convertToDays(uvPeriod: UnitValue) {
    if (!this.periodUnits) {
      this.periodUnits = this.unitService.getUnitsByUnitTypeName('Period');
    }
    const unit = this.periodUnits.find(aUnit => aUnit.id.toString() === uvPeriod.unit);
    if (unit) {
      if (unit.factor === 'V') return uvPeriod.value; // already days

      const modArr = unit.factor.match(/V(.)(\d+)/);
      if (modArr && modArr.length === 3) {
        switch (modArr[1]) {
          case '*':
            return uvPeriod.value * Number(modArr[2]);
          case '/':
            return uvPeriod.value / Number(modArr[2]);
        }
      }
    }
    return null;
  }

  getCapexItemById(index: number): FinancialCapex {
    if (this.capex) {
      if (index > -1 && index < this.capex.length) {
        return FinancialCapex.deepCopy(this.capex[index]);
      }
    }
  }

  addCapex(capex: FinancialCapex) {
    if (this.capex) {
      if (Utils.nameIsUnique(capex.name, this.capex)) {
        this.capex.push(capex);
        this.capexUpdatedNotifier.next(true);
        this.unsavedChangesNotifier.next(true);
        return true;
      } else {
        this.toastrService.error(this.translationService.get('SERVICE.FINANCIAL_CAPEX.CAPEX_EXISTS_ERROR'), this.translationService.get('SERVICE.FINANCIAL_CAPEX.CAPEX_ADD_ERROR'));
        return false;
      }
    }
    throw Error(this.translationService.get('SERVICE.FINANCIAL_CAPEX.CAPEX_SAVE_ERROR'));
  }

  getQuantityUnitTypeName(connectionType: string): string[] {
    switch (connectionType) {
      case PeriodType.PERIOD:
        return ['Period'];
      case PeriodType.ELECTRIC:
        return ['Energy'];
      case PeriodType.HEAT:
        return ['Thermal_Quantity'];
      case PeriodType.FEEDAMOUNT:
      case PeriodType.SOLID:
        return ['Mass'];
      case PeriodType.WHOLE:
      case PeriodType.THIN:
        return ['Mass', 'Volume_Liquid'];
      case PeriodType.BIOGAS:
      case PeriodType.BIOMETHANE:
        return ['Volume_Gas'];
    }
  }

  isConnectionTypeSafe(connectionType: string): boolean {
    const products = this.projectCommService.getProjectPlantSpecifications().generatedProducts;
    switch (connectionType) {
      // case PeriodType.PERIOD:
      case PeriodType.FEEDAMOUNT:
      case PeriodType.BIOGAS:
        return true;
      case PeriodType.ELECTRIC:
        return products.some(prod => prod.type === ProductType.Electric);
      case PeriodType.HEAT:
        return products.some(prod => prod.type === ProductType.Heat);
      case PeriodType.SOLID:
        return products.some(prod => prod.type === ProductType.SolidDigestate);
      case PeriodType.WHOLE:
        return true;
      // return products.some(prod => prod.type === ProductType.WholeDigestate);
      case PeriodType.THIN:
        return products.some(prod => prod.type === ProductType.ThinDigestate);
      case PeriodType.BIOMETHANE:
        return products.some(prod => prod.type === ProductType.Biometh);
      default:
        return false;
    }
  }

  extractQuantityUnitValue(quantityUnit: string, connectionType: string): string {
    const parsedQuantityUnit = Unit.parseUnitDisplayName(quantityUnit);
    const periodUnitTypeNames = this.getQuantityUnitTypeName(connectionType);
    let retVal: string;

    for (const typeName of periodUnitTypeNames) {
      const unitId = this.unitService.getUnitByNameAndUnitTypeName(parsedQuantityUnit, typeName)?.id;
      if (unitId) {
        retVal = unitId;
        break;
      }
    }

    return retVal;
  }

  addCapexFromRow(row: CapexImportRow) {
    const cItem: FinancialCapex = new FinancialCapex();
    cItem.lifetime = this.getDefaultLifetime();
    cItem.name = row['name'];
    cItem.description = row['description'];

    if (row['connection']) {
      cItem.connectionToggle = true;

      if (this.isConnectionTypeSafe(row['connection'])) {
        cItem.connectionType = row['connection'];

        /* cost should get value only if the connection type is safe */
        if (row['cost'] !== null && row['cost'] !== undefined) {
          cItem.cost = new UnitValue();
          cItem.cost.unit = this.unitService.getUnitByNameAndUnitTypeName(row['currency'], 'Currency')?.id;
          if (!cItem.cost.unit) {
            this.toastrService.error('failed to find target unit for capex item ' + cItem.name);
          }
          cItem.cost.value = row['cost'];
        }

        cItem.quantity = new UnitValue();
        cItem.quantity.unit = this.extractQuantityUnitValue(row['quantity_unit'], row['connection']);
        if (!cItem.quantity.unit) {
          this.toastrService.error('failed to find quantity unit `' + row['quantity_unit'] + '` for capex item ' + cItem.name);
        }
        cItem.quantity.value = row['quantity'];

        cItem.period = new UnitValue();
        const parsedUnit = Unit.parseUnitDisplayName(row['period_unit']);
        cItem.period.unit = this.unitService.getUnitByNameAndUnitTypeName(parsedUnit, 'Period')?.id;
        if (!cItem.period.unit) {
          this.toastrService.error('failed to find period unit `' + row['period_unit'] + '` for capex item ' + cItem.name);
        }
        cItem.period.value = 1;
      } else {
        this.toastrService.error('failed to find a product for connection type ' + row['connection'])
      }
    } else {
      /* cost should get value if there is no connection */
      if (row['cost'] !== null && row['cost'] !== undefined) {
        cItem.cost = new UnitValue();
        cItem.cost.unit = this.unitService.getUnitByNameAndUnitTypeName(row['currency'], 'Currency')?.id;
        if (!cItem.cost.unit) {
          this.toastrService.error('failed to find target unit for capex item ' + cItem.name);
        }
        cItem.cost.value = row['cost'];
      }
    }

    if (row['lifetime'] !== null && row['lifetime'] !== undefined) {
      cItem.lifetime = new UnitValue();
      cItem.lifetime.unit = this.unitService.getUnitByNameAndUnitTypeName('years', 'Period').id;
      cItem.lifetime.value = row['lifetime'];
    }

    this.addCapex(cItem);
  }

  updateCapexItem(capex: FinancialCapex, index: number) {
    if (this.capex && index > -1 && index < this.capex.length) {
      if (Utils.nameIsUnique(capex.name, this.capex, index)) {
        // when a capex is edited, removed it's estimated status
        if (this.valuesChanged(capex, index)) {
          capex.estimated = false;
        }
        this.capex[index] = capex;
        this.capexUpdatedNotifier.next(true);
        this.unsavedChangesNotifier.next(true);
        return true;
      }
      this.toastrService.error(this.translationService.get('SERVICE.FINANCIAL_CAPEX.CAPEX_EXISTS_ERROR'), this.translationService.get('SERVICE.FINANCIAL_CAPEX.CAPEX_ADD_ERROR'));
      return false;
    }
    throw Error(this.translationService.get('SERVICE.FINANCIAL_CAPEX.CAPEX_SAVE_ERROR'));
  }

  valuesChanged(capex, index) {
    const ignored = ['description', 'estimated'];
    for (const prop in capex) {
      if (!ignored.includes(prop)) {
        if (capex[prop] instanceof UnitValue) {
          if (this.capex[index][prop].value !== capex[prop].value || this.capex[index][prop].unit !== capex[prop].unit) {
            return true;
          }
        } else if (this.capex[index][prop] !== capex[prop]) {
          return true;
        }
      }
    }
    return false;
  }

  removeCapex(index: number) {
    if (this.capex && index > -1 && index < this.capex.length) {
      this.capex.splice(index, 1);
      this.capexUpdatedNotifier.next(true);
      this.unsavedChangesNotifier.next(true);
    }
  }

  saveCapex() {
    this.financialService.updateSavedTotalCapexUnit(this.savedTotalCapexUnit);
    this.financialService.updatedDefaultLifetime(this.defaultLifetime);
    this.financialService.saveCapex(this.capex);
    this.unsavedChangesNotifier.next(false);
  }

  resetCapex() {
    this.capex = this.financialService.getCapex();
    this.setDefaultLifetime();
    this.savedTotalCapexUnit = this.financialService.getSavedTotalCapexUnit();
    this.capexUpdatedNotifier.next(true);
    this.unsavedChangesNotifier.next(true);
  }

  getTotalCapexInTargetCur() {
    const totalInTargetCur = new UnitValue();
    const totalInCad = this.financialService.getTotalCapexAmountInCad(this.capex);
    let targetCurUnit: Unit;
    targetCurUnit = this.financialService.getCurrencyUnitById(this.getTargetUnitForTotalCapex());
    if (!targetCurUnit) {
      throw Error('failed to find target unit for total capex');
    }
    totalInTargetCur.unit = targetCurUnit.id;
    totalInTargetCur.value = targetCurUnit.convertFromBase(totalInCad.value);
    return totalInTargetCur;
  }

  updateTotalCapexSavedUnit(unitId: string) {
    if (unitId !== this.savedTotalCapexUnit) {
      this.savedTotalCapexUnit = unitId;
      this.capexUpdatedNotifier.next(true);
      this.unsavedChangesNotifier.next(true);
    }
  }

  getTargetUnitForTotalCapex() {
    let targetUnit = '';
    targetUnit = this.savedTotalCapexUnit;
    if (!targetUnit) { // if saved unit is not avail service level
      targetUnit = this.financialService.getSavedTotalCapexUnit();
    }
    if (!targetUnit) { // if saved is null try this
      targetUnit = this.projectService.getDefaultCurrency();
    }
    if (!targetUnit) {// if default is null try this
      targetUnit = this.getFirstCapexUnit();
    }
    if (!targetUnit) { // if no first capex unit, try this
      targetUnit = this.financialService.getUsdUnitId(); // USD
    }
    return targetUnit;
  }

  private getFirstCapexUnit() {
    if (this.capex && this.capex.length > 0) {
      const firstCapex = this.capex[0];
      if (firstCapex.cost && firstCapex.cost.unit) {
        return firstCapex.cost.unit;
      }
    }
  }

  getDefaultLifetime(): UnitValue {
    return UnitValue.deepCopy(this.defaultLifetime);
  }

  updateDefaultLifetime(defaultLifetime: UnitValue) {
    this.defaultLifetime = defaultLifetime;
    this.unsavedChangesNotifier.next(true);
  }

  clearAll() {
    this.capex = [];
    this.capexUpdatedNotifier.next(true);
    this.unsavedChangesNotifier.next(true);
  }

  private setDefaultLifetime() {
    this.defaultLifetime = this.financialService.getDefaultLifetime();
  }

  private convertCapexItemToTargetCur(item: FinancialCapex) {
    const targetUnit = this.financialService.getCurrencyUnitById(
      this.getTargetUnitForTotalCapex()
    );
    if (item && new UnitValueValidation(item.cost).isValid()) {
      const currentUnit = this.financialService.getCurrencyUnitById(item.cost.unit);
      const costInCad = currentUnit.convertToBase(item.cost.value);
      const costInTargetUnit = Utils.roundToPrecision(targetUnit.convertFromBase(costInCad), 2);
      item.cost = new UnitValue({ value: costInTargetUnit, unit: targetUnit.id });
    }
    return item;
  }

  private createCapexItemFromEstItem(estItem: EstimationItem) {
    let capexItem = new FinancialCapex();
    capexItem.name = this.translationService.get(estItem.name);
    capexItem.cost = new UnitValue({
      value: estItem.cost,
      unit: estItem.costUnit // as of now this is always in euro
    });
    capexItem = this.convertCapexItemToTargetCur(capexItem);
    capexItem.lifetime = this.defaultLifetime;
    capexItem.estimated = true;
    return capexItem;
  }

  estimateCapex() {
    const projectValidation = this.validationService.getProjectValidation();
    if (projectValidation.plantSpecificationsPercentage === 100
      && projectValidation.feedstockPercentage === 100
    ) {
      if (this.financialService.checkForBiomethOrElec()) {
        return this.commService.estimateCapex()
          .pipe(take(1)).subscribe(est => {
            if (est) this.sanitizeResults(est);
            if (est && Object.keys(est).includes('RESULTS.DATA.CAPEX')) {
              this.capex = [];
              const capexEst = est['RESULTS.DATA.CAPEX'];
              // capex is currently calculated in euro
              const euroUnitId = this.unitService.getUnitByName('EUR').id;
              const totalEstItem = new EstimationItem('RESULTS.DATA.TOTAL_CAPEX', 0, euroUnitId);
              const isProjectInput = this.projectCommService.isProjectInput();
              for (const item of Object.keys(capexEst)) {
                const estItem = new EstimationItem(item, capexEst[item], euroUnitId);
                const capexItem = this.createCapexItemFromEstItem(estItem);
                // Combine all capex items when called from project input form
                if (!isProjectInput) {
                  this.capex.push(capexItem);
                } else {
                  totalEstItem.cost += estItem.cost;
                }
              }
              if (isProjectInput) {
                this.capex.push(this.createCapexItemFromEstItem(totalEstItem));
                this.saveCapex();
              }
              this.capexUpdatedNotifier.next(true);
              this.unsavedChangesNotifier.next(true);
            }
          });
      } else {
        this.toastrService.warning(
          this.translationService.get('SERVICE.FINANCIAL_CAPEX.NO_PRODUCT_ERROR'),
          this.translationService.get('SERVICE.FINANCIAL_CAPEX.CANT_ESTIMATE_ERROR')
        );
      }
    } else {
      this.showProgPopup(projectValidation);
    }
  }

  private sanitizeResults(results) {
    // pretty sure these need to be let instead of const
    // do not update to const
    for (let key of Object.keys(results)) {
      let newKey = this.updateKey(results, key);
      if (results[newKey] && typeof results[newKey] === 'object') {
        let children = Object.keys(results[newKey]);
        if (children && children.length > 0) {
          results[newKey] = this.sanitizeResults(results[newKey]);
        }
      } else if (results[newKey] && typeof results[newKey] === 'string') {
        results[newKey] = results[newKey].replace(/~/g, '.');
      }
    }
    return results;
  }

  private updateKey(obj, oldKey) {
    const newKey = oldKey.replace(/~/g, '.');

    if (oldKey !== newKey) {
      Object.defineProperty(obj, newKey,
        Object.getOwnPropertyDescriptor(obj, oldKey));
      delete obj[oldKey];
    }
    return newKey;
  }

  private showProgPopup(projectValidation: ProjectValidation) {
    // TODO this is temp, push to array and implement prop pop up data in to be done properly (ask Kyle)
    let section = ValidationSectionName.All;
    let warning: string;
    if (projectValidation.plantSpecificationsPercentage !== 100) {
      section = ValidationSectionName.PlantSpecifications;
      warning = this.translationService.get('SERVICE.FINANCIAL_CAPEX.INVALID_PLANT_SPEC');
    } else if (projectValidation.feedstockPercentage !== 100) {
      if (this.validationService.checkFeedstockCharacteristics()) {
        this.toastrService.warning(this.translationService.get('SERVICE.FINANCIAL_CAPEX.MISSING_CHARACTERISTICS'));
      }
      section = ValidationSectionName.Feedstock;
      warning = this.translationService.get('SERVICE.FINANCIAL_CAPEX.INVALID_FEEDSTOCK');
    }
    if (warning) {
      this.toastrService.warning(warning);
    }
    this.dialog.open(ProgressPopupDialogComponent, {
      width: '800px',
      height: '85vh',
      data: {
        projectValidation: projectValidation,
        section: section,
        onlyShowInvalid: true
      }
    });
  }

  public getRateFor(type: string) {
    const rates = this.financialService.getRates();
    if (rates && rates[type]) {
      return rates[type];
    }
    return 0;
  }

  /**
   * Return the default percentages for the capex est. Uses
   * efaultCapexOpexEstNames as object keys.
   */
  static getDefaultCapexPercentages(): { [key in DefaultCapexOpexEstNames]?: number; } {
    const percentages: { [key in DefaultCapexOpexEstNames]?: number; } = Object();
    const totalDefaultCapexPercent = this.getTotalDefaultCapexEstPercent();
    for (const key in DefaultCapexEstPercentages.DEFAULT_CAPEX_EST_PERCENTAGES) {
      percentages[key] = DefaultCapexEstPercentages.DEFAULT_CAPEX_EST_PERCENTAGES[key]
        / totalDefaultCapexPercent * 100;
    }

    return percentages;
  }

  /**
   * Get the total of the default capex est percentages.
   */
  static getTotalDefaultCapexEstPercent(): number {
    let runningTotal = 0;

    for (const key in DefaultCapexEstPercentages.DEFAULT_CAPEX_EST_PERCENTAGES) {
      runningTotal += DefaultCapexEstPercentages.DEFAULT_CAPEX_EST_PERCENTAGES[key];
    }

    return runningTotal;
  }
}
